import React, { useEffect, useState } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { useIntl } from 'react-intl'
import _ from 'lodash'
import { toast } from 'react-toastify'
import styled from '@emotion/styled'
import { Button, CircularProgress, LinearProgress } from '@mui/material'
import { Box } from '@mui/system'
import ImportExportIcon from '@mui/icons-material/ImportExport'
import { H3, H6 } from '../../../layouts/TextStyles'
import { testApiSlice } from '../../../features/section/testSlice'
import { videoLessonApiSlice } from '../../../features/section/videoLessonSlice'
import { capitalize } from '../../../utils/functions'
import Breadcrumb from '../../../components/Breadcrumb'
import DndList from './components/DndList'
import AddTestDialog from './components/Dialogs/AddTestDialog'
import AddVideoDialog from './components/Dialogs/AddVideoDialog'
import { sectionApiSlice } from '../../../features/section/sectionSlice'
import storage from '../../../utils/storageService'
import TopicHomeworkList from './components/TopicHomeworkList'
import TopicBookPages from './components/TopicBookPages'
import { homeworkApiSlice } from '../../../features/section/homeworkSlice'
import { ui } from '../../../utils/uiTokens'

const styles = {
    section: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        mt: 4,
    },

    orderButton: {
        display: 'flex',
        color: ui.pink,
        alignItems: 'center',
        cursor: 'pointer',
    },

    dndBox: {
        display: 'flex',
        maxHeight: '600px',
        overflow: 'auto',
        mb: 2,
    },

    loading: {
        width: '100%',
        mb: 2,
    },

    flex: {
        display: 'flex',
    },
}

const TopicSettingsPage = () => {
    const { formatMessage, locale } = useIntl()
    const { state } = useLocation()
    const navigate = useNavigate()
    const { courseTopicId } = useParams()
    const [editDnd, setEditDnd] = useState(false)
    const [resources, setResources] = useState([])
    const [editRow, setEditRow] = useState(null)
    const {
        data: sections = [],
        isFetching,
        refetch,
    } = sectionApiSlice.useGetSectionsByCourseTopicQuery(+courseTopicId)
    const [updateSectionOrders, { isLoading: isUpdating }] =
        sectionApiSlice.useUpdateSectionOrdersMutation()
    const [deleteTest] = testApiSlice.useDeleteTestMutation()
    const [deleteVideoLesson] =
        videoLessonApiSlice.useDeleteVideoLessonMutation()
    const { data: homework = [], refetch: refetchHomework } =
        homeworkApiSlice.useGetHomeworkByCourseTopicQuery(+courseTopicId)

    const addResource = (items) => {
        setResources(items)
    }

    const cancelDnd = () => {
        setResources(sections)
    }

    const saveDnd = async () => {
        try {
            await updateSectionOrders(resources).unwrap()
            toast.success(formatMessage({ id: 'savedSuccess' }))
            setEditDnd(false)
            refetch()
        } catch (error) { }
    }

    const addQuestions = (element) => {
        navigate(`/questions/${element.id}`, { state: { test: element } })
    }

    const addEditRow = (element) => {
        setEditRow(element)
    }

    const handleDelete = async (item) => {
        if (item.type === 'videoLesson') {
            await toast.promise(deleteVideoLesson(item.id).unwrap(), {
                pending: formatMessage({ id: 'deleting' }),
                success: formatMessage({ id: 'deletedSuccess' }),
                error: formatMessage({ id: 'deleteFail' }),
            })
            refetch()
        } else if (item.type === 'topicTest') {
            await toast.promise(deleteTest(item.id).unwrap(), {
                pending: formatMessage({ id: 'deleting' }),
                success: formatMessage({ id: 'deletedSuccess' }),
                error: formatMessage({ id: 'deleteFail' }),
            })

            storage?.remove(item.id)
            refetch()
        }
    }

    useEffect(() => {
        if (sections.length > 0) {
            setResources(sections)
        }
    }, [sections])


    return (
        <Wrapper>
            <Breadcrumb
                links={[
                    formatMessage({ id: 'courses' }),
                    state?.course?.[`title${capitalize(locale)}`],
                    state?.subject?.[`title${capitalize(locale)}`],
                    state?.topic?.[`title${capitalize(locale)}`],
                ]}
            />

            <Content>
                <Box sx={{ width: 'max-content' }}>
                    <Box mb={4}>
                        <Box sx={styles.section}>
                            <Box>
                                <H3>
                                    {
                                        state?.topic?.[
                                        `title${capitalize(locale)}`
                                        ]
                                    }
                                </H3>

                                <H6>
                                    {formatMessage({ id: 'videoAndTopicTest' })}
                                </H6>
                            </Box>

                            <Button
                                sx={styles.orderButton}
                                endIcon={<ImportExportIcon />}
                                onClick={() => setEditDnd(!editDnd)}
                            >
                                {formatMessage({ id: 'changeOrder' })}
                            </Button>
                        </Box>
                    </Box>

                    {isFetching && <LinearProgress sx={styles.loading} />}

                    <Box sx={styles.dndBox}>
                        <DndList
                            items={resources}
                            editDnd={editDnd}
                            setItems={(elements) => addResource(elements)}
                            addQuestions={(element) => addQuestions(element)}
                            setEditRow={(element) => addEditRow(element)}
                            onDelete={handleDelete}
                        />
                    </Box>

                    <Box sx={styles.section}>
                        <Box sx={styles.flex}>
                            <AddTestDialog
                                courseTopicId={Number(courseTopicId)}
                                getSections={() => refetch()}
                                editTest={
                                    editRow?.type === 'topicTest' ? editRow : ''
                                }
                                setEditRow={(values) => setEditRow(values)}
                            />

                            <AddVideoDialog
                                courseTopicId={Number(courseTopicId)}
                                getSections={() => refetch()}
                                editVideo={
                                    editRow?.type === 'videoLesson'
                                        ? editRow
                                        : ''
                                }
                                setEditRow={(values) => setEditRow(values)}
                            />
                        </Box>

                        {editDnd && (
                            <Box>
                                <Button
                                    variant="outlined"
                                    sx={{ mr: 2 }}
                                    onClick={() => cancelDnd()}
                                >
                                    {formatMessage({ id: 'cancel' })}
                                </Button>

                                <Button
                                    variant="contained"
                                    onClick={() => saveDnd()}
                                    disabled={
                                        _.isEqual(sections, resources) ||
                                        isUpdating
                                    }
                                >
                                    {isUpdating ? (
                                        <CircularProgress
                                            size={20}
                                            sx={{ mr: 1 }}
                                        />
                                    ) : (
                                        formatMessage({ id: 'save' })
                                    )}
                                </Button>
                            </Box>
                        )}
                    </Box>
                    <Box>
                        <TopicHomeworkList homework={homework} refetchHomework={refetchHomework} />
                    </Box>
                    <TopicBookPages
                        courseTopicId={Number(courseTopicId)}
                        topicTitle={(locale === 'ru' ? state?.topic?.titleRu : state?.topic?.titleKz) || state?.topic?.titleKz || state?.topic?.titleRu || ''}
                        lang={String(state?.subject?.language || state?.course?.language || (locale === 'ru' ? 'ru' : 'kz')).toLowerCase().startsWith('ru') ? 'ru' : 'kz'}
                    />
                </Box>
            </Content>
        </Wrapper>
    )
}

export default TopicSettingsPage

const Wrapper = styled.div`
    margin: 10px;
    border-radius: 16px;
`

const Content = styled.div`
    background-color: ${ui.card};
    border-radius: 16px;
    padding: 20px;
    min-height: 80vh;
`
